import { useState } from 'react';

import {
  isProForbiddenError,
  proErrorMessage,
  proSheetModel,
} from '@splitfiesta/core';
import { useTranslation } from '@splitfiesta/i18n';
import type { GroupWithDashboard } from '@splitfiesta/shared';

import {
  useProGroupMutations,
  useProGroupsQuery,
} from '@/hooks/queries/useProGroups';
import type { ApiError } from '@/hooks/useApi';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';

import { useUser } from '@/stores/authStore';

import { toast } from '@/utils/toast';

import { ProGroupDialog } from './ProGroupDialog';
import { proFallbackMessage } from './proFallbackMessage';

interface ProGroupDialogContainerProps {
  isOpen: boolean;
  onClose: () => void;
  /** An add or remove went through; called just before the dialog closes. */
  onChanged?: () => void;
  /** All the dialog reads; `balances` names the supporters. */
  group: Pick<GroupWithDashboard, 'id' | 'name' | 'pro'> & {
    balances?: Array<{ userId: string; userName: string }>;
  };
}

export function ProGroupDialogContainer({
  isOpen,
  onClose,
  onChanged,
  group,
}: ProGroupDialogContainerProps) {
  const { t, i18n } = useTranslation();
  const user = useUser();
  const { isOnline } = useNetworkStatus();
  const {
    data: proGroups,
    isError: listFailed,
    isFetching: retrying,
    refetch,
  } = useProGroupsQuery(isOpen && !!user);
  const { add, remove } = useProGroupMutations();
  const [serverMessage, setServerMessage] = useState<string>();
  // A mutation that settles after a close can leave a message behind.
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (wasOpen !== isOpen) {
    setWasOpen(isOpen);
    if (isOpen) setServerMessage(undefined);
  }

  const close = () => {
    setServerMessage(undefined);
    onClose();
  };

  const { variant, replaceCandidates, groupIsPro } = proSheetModel({
    user,
    group,
    proGroups: proGroups?.items,
    listFailed,
  });

  const handleError = (error: ApiError) => {
    if (isProForbiddenError(error)) {
      close();
      toast.error(t(error.translationKey ?? 'errors:api.status.403'));
      return;
    }
    setServerMessage(
      proErrorMessage(error, t, i18n.language, other =>
        proFallbackMessage(other, t)
      )
    );
  };

  const run = async (call: () => Promise<unknown>) => {
    if (!isOnline) return;
    setServerMessage(undefined);
    try {
      await call();
      onChanged?.();
      close();
    } catch (error) {
      handleError(error as ApiError);
    }
  };

  return (
    <ProGroupDialog
      isOpen={isOpen}
      onClose={close}
      groupId={group.id}
      groupName={group.name}
      variant={variant}
      groupIsPro={groupIsPro}
      isOnline={isOnline}
      busy={add.isPending || remove.isPending}
      retrying={retrying}
      serverMessage={serverMessage}
      onAdd={replaceGroupId =>
        void run(() =>
          add.mutateAsync(
            replaceGroupId
              ? { groupId: group.id, replaceGroupId }
              : { groupId: group.id }
          )
        )
      }
      onRemove={() => void run(() => remove.mutateAsync(group.id))}
      onRetry={() => void refetch()}
      replaceCandidates={replaceCandidates}
    />
  );
}
